import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Copy, RefreshCw, Check } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { confirmDialog } from "../lib/confirm";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";

export default function ICalSyncPanel() {
  const { lang } = useI18n();
  const de = lang !== "en";
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/me/ical")
      .then((r) => setUrl(r.data.url))
      .catch((e) => console.debug("ical load skipped:", e?.response?.status));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(de ? "Feed-Adresse kopiert." : "Feed URL copied.");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error(de ? "Kopieren nicht möglich — bitte manuell markieren." : "Copying failed — please select manually.");
    }
  };

  const rotate = async () => {
    if (!(await confirmDialog({
      title: de ? "Feed-Link neu erzeugen?" : "Generate a new feed link?",
      description: de
        ? "Der alte Link wird sofort ungültig. Bestehende Kalender-Abonnements müssen einmalig neu hinzugefügt werden."
        : "The old link becomes invalid immediately. Existing calendar subscriptions need to be re-added once.",
      confirmText: de ? "Neu erzeugen" : "Generate",
    }))) return;
    setRotating(true);
    try {
      const { data } = await api.post("/me/ical/rotate");
      setUrl(data.url);
      toast.success(de ? "Neuer Feed-Link erzeugt." : "New feed link generated.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setRotating(false);
    }
  };

  if (!url) return null;

  return (
    <div
      className="bg-white border border-navy/10 p-6"
      data-testid="ical-sync-panel"
    >
      <div className="flex items-start gap-3 mb-4">
        <CalendarDays strokeWidth={1.4} className="w-7 h-7 shrink-0 mt-0.5" style={{ color: BRAND_COLORS.gold }} />
        <div className="flex-1">
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>{de ? "Kalender-Sync" : "Calendar sync"}</div>
          <div className="font-display text-xl text-navy leading-snug">
            {de ? "In Apple/Google-Kalender abonnieren" : "Subscribe in Apple / Google Calendar"}
          </div>
          <p className="text-[13px] text-navy-dark/70 mt-1 leading-relaxed">
            {de
              ? "Ihre geplanten Abwesenheiten & Ankünfte erscheinen automatisch in Ihrem Kalender — Änderungen werden alle 15 Minuten synchronisiert."
              : "Your planned absences & arrivals appear automatically in your calendar — changes sync every 15 minutes."}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          data-testid="ical-url-input"
          value={url}
          readOnly
          onClick={(e) => e.target.select()}
          className="flex-1 bg-ivory/70 border border-navy/15 px-3 py-2.5 text-[12px] font-mono text-navy-dark focus:border-gold focus:outline-none"
        />
        <button
          type="button"
          data-testid="ical-copy-btn"
          onClick={copy}
          className="border border-navy text-navy px-3 py-2.5 text-[11px] tracking-widest uppercase hover:bg-navy hover:text-ivory transition-colors flex items-center gap-2"
          aria-label={de ? "Feed-Adresse kopieren" : "Copy feed URL"}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? (de ? "Kopiert" : "Copied") : (de ? "Kopieren" : "Copy")}</span>
        </button>
      </div>

      <button
        type="button"
        data-testid="ical-instructions-toggle"
        onClick={() => setExpanded((v) => !v)}
        className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 hover:text-navy transition-colors underline underline-offset-4"
      >
        {expanded ? (de ? "Anleitung schließen" : "Close instructions") : (de ? "Einrichten auf Apple & Google Kalender" : "Set up on Apple & Google Calendar")}
      </button>

      {expanded && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5 text-[12.5px] text-navy-dark/90 leading-relaxed" data-testid="ical-instructions">
          <div>
            <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Apple Kalender (iPhone / Mac)</div>
            <ol className="space-y-1 list-decimal ml-5">
              <li>Öffnen Sie <em>Einstellungen → Kalender → Accounts → Account hinzufügen → Andere</em>.</li>
              <li>Wählen Sie <em>„Kalenderabo hinzufügen"</em>.</li>
              <li>Fügen Sie die Feed-Adresse oben ein und tippen Sie <em>Weiter</em>.</li>
              <li>Bestätigen Sie mit <em>Sichern</em>.</li>
            </ol>
          </div>
          <div>
            <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Google Kalender</div>
            <ol className="space-y-1 list-decimal ml-5">
              <li>Öffnen Sie Google Kalender im Browser.</li>
              <li>Links neben <em>„Weitere Kalender"</em> auf das <em>+</em> klicken.</li>
              <li>Wählen Sie <em>„Per URL"</em>.</li>
              <li>Fügen Sie die Feed-Adresse ein und klicken Sie <em>„Kalender hinzufügen"</em>.</li>
            </ol>
          </div>
          <div className="md:col-span-2 flex items-start justify-between gap-3 pt-3 border-t border-navy/10">
            <p className="text-[11.5px] text-navy-dark/60 italic max-w-md leading-relaxed">
              Die Adresse ist Ihr persönlicher Schlüssel. Teilen Sie sie nicht — bei Verlust neu erzeugen.
            </p>
            <button
              type="button"
              data-testid="ical-rotate-btn"
              onClick={rotate}
              disabled={rotating}
              className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 hover:text-navy transition-colors inline-flex items-center gap-2 disabled:opacity-50 shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} />
              Neuen Link erzeugen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
